import type { ChangeEvent, KeyboardEvent } from 'react';
import { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';

//utils
import { emailValid } from '@/lib/regex';

import Icon from '@/components/base/Icon';
//components
import Input from '@/components/base/Input';
import Label from '@/components/base/Label';
import { Trans } from '@/components/tools/translation';

type InputEmailsProps = {
    onSetEmails: (email: string) => void;
    onRemoveEmail: (email: string[]) => void;
    emails: string[];
    errors?: string;
    disabled?: boolean;
    className?: string;
    required?: boolean;
};
export const InputEmails = ({
    onSetEmails,
    onRemoveEmail,
    className = 'mb-5',
    emails,
    errors,
    disabled,
    required = true
}: InputEmailsProps) => {
    const { t } = useTranslation('errors');
    const [inputValue, setInputValue] = useState<string>('');
    const [shouldDisplayNotice, setShouldDisplayNotice] = useState<boolean>(false);
    const [error, setError] = useState<string>(errors);

    const isEmailValid = (email: string) => {
        if (!email) return false;
        return new RegExp(emailValid).test(email);
    };

    useEffect(() => {
        if (errors) {
            setError(errors);
        }
    }, [errors]);

    useDebounce(
        async () => {
            if (!inputValue) {
                return setShouldDisplayNotice(false);
            }
            if (isEmailValid(inputValue) && !error) {
                if (shouldDisplayNotice) return;
                return setShouldDisplayNotice(true);
            }
            return setShouldDisplayNotice(false);
        },
        200,
        [inputValue]
    );

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (error && isEmailValid(event.target.value)) {
            setError(null);
        }
        setInputValue(event.target.value);
    };

    const onPressEnter = (event: KeyboardEvent<HTMLInputElement>) => {
        const element = event.target as HTMLInputElement;
        if (event.key === 'Enter') {
            event.preventDefault();
            if (emails.includes(element.value)) {
                return setError(t('error_reports.error_email_already_used'));
            } else if (!isEmailValid(element.value)) {
                event.preventDefault();
                return setError(t('error_email_string'));
            }
            onSetEmails(element.value);
            setInputValue('');
            setError(null);
        }
    };

    const handleRemoveEmail = (value: string) => {
        const filterEmails = emails.filter((item) => item !== value);
        onRemoveEmail(filterEmails);
    };

    return (
        <div className={clsx('w-full', className)}>
            <Label htmlFor="emails" required={required}>
                {t('commons:input_emails.label_send_email_to')}
            </Label>
            <Input
                id="emails"
                placeholder={t('commons:input_emails.placeholder_send_email_to')}
                type="email"
                autoComplete="off"
                value={inputValue}
                onChange={handleChange}
                onKeyDown={onPressEnter}
                className="mb-2"
                error={error}
                disabled={disabled}
            />
            {shouldDisplayNotice ? (
                <span className="mb-1.5 block text-xs text-icoBlue" data-testid="add-email-notice">
                    <Trans
                        i18nKey="commons:input_emails.emails_input_notice"
                        values={{ value: inputValue }}
                    />
                </span>
            ) : null}

            {emails?.length ? (
                <ul className="flex flex-wrap gap-2" data-testid="email-list">
                    {emails.map((emailItem: string, index: number) => (
                        <li
                            key={index}
                            className="flex w-auto items-center rounded-full bg-icoBlue-100 px-2 py-2 dark:bg-icoBlue/20">
                            <span className="mr-2 text-sm text-icoBlue">{emailItem}</span>
                            <button
                                data-testid="remove-button"
                                disabled={disabled}
                                className={clsx('flex items-center rounded-full text-icoBlue', {
                                    'cursor-not-allowed': disabled,
                                    'hover:text-icoBlue-200': !disabled
                                })}
                                onClick={() => handleRemoveEmail(emailItem)}>
                                <Icon icon="close" />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
};
